/**
 * SDS Variants management — ported from Designer
 * Color/size variant extraction, selection, and UI rendering
 */

import { store } from '../core/store.js'
import { eventBus, EVENTS } from '../core/event-bus.js'
import { createLogger } from '../utils/logger.js'

const log = createLogger('variants')

// Cached data
let cachedVariants = []
let useMmsVariants = false
let selectedColor = null
let selectedSize = null

/**
 * Load and render the variants list
 */
export function loadVariants() {
  const sdk = store.getState('design.sdkInstance')
  if (!sdk) {
    log.warn('SDK not available')
    return
  }

  try {
    const currentProduct = store.getState('design.currentProduct')
    let sdkVariants = []
    try {
      sdkVariants = sdk.getVariants() || []
    } catch (e) {
      log.warn('sdk.getVariants() failed, using mms data only:', e.message)
    }
    const variants = currentProduct?.variants || sdkVariants

    useMmsVariants = !!currentProduct?.variants
    cachedVariants = variants

    if (variants.length === 0) {
      log.info('No variants available')
      return
    }

    const colors = extractColors(variants)
    const sizes = extractSizes(variants)

    renderColors(colors)
    renderSizes(sizes)

    log.info(`Loaded ${colors.length} colors, ${sizes.length} sizes`)
  } catch (error) {
    console.warn('Failed to load variants:', error)
  }
}

/**
 * Extract unique colors
 */
function extractColors(variants) {
  const colorMap = new Map()

  variants.forEach((v) => {
    if (!v.color) return

    const key = typeof v.color === 'object' ? JSON.stringify(v.color) : String(v.color)
    const display = typeof v.color === 'object' ? v.color.color_name || v.color.name : v.color
    const value = typeof v.color === 'object' ? v.color.color : getColorValue(v.color)

    if (!colorMap.has(key)) {
      colorMap.set(key, {
        display,
        value,
        // NOTE: v.price is the SDS supplier reference price (variant-level).
        // The displayed MMS selling price is product-level — switchToVariant
        // resolves it from design.currentProduct.price, not from this field.
        price: v.price || v.mms_back_end_price_usd,
        selected: false,
      })
    }
  })

  const colors = Array.from(colorMap.values())
  if (colors.length > 0) {
    colors[0].selected = true
    selectedColor = colors[0].display
  }

  return colors
}

/**
 * Extract unique sizes
 */
function extractSizes(variants) {
  const sizeMap = new Map()

  variants.forEach((v) => {
    if (!v.size) return

    const key = typeof v.size === 'object' ? JSON.stringify(v.size) : String(v.size)
    const display = typeof v.size === 'object' ? v.size.name || v.size.title : v.size

    if (!sizeMap.has(key)) {
      sizeMap.set(key, {
        display: fixSizeEncoding(display),
        // NOTE: SDS supplier reference price (variant-level), not the MMS
        // selling price — see switchToVariant for the product-level price.
        price: v.price || v.mms_back_end_price_usd,
        selected: false,
      })
    }
  })

  const sizes = Array.from(sizeMap.values())
  if (sizes.length > 0) {
    sizes[0].selected = true
    selectedSize = sizes[0].display
  }

  return sizes
}

/**
 * Render color options
 */
function renderColors(colors) {
  const container = document.getElementById('colorQuickSelect')
  if (!container || colors.length === 0) return

  container.innerHTML = colors
    .map((c) => {
      const active = c.selected ? ' active' : ''
      const colorBlock = c.value
        ? `<span style="display:inline-block;width:16px;height:16px;background:${c.value};border:1px solid var(--border-strong);margin-right:6px;border-radius:2px;"></span>`
        : ''
      return `<button class="variant-option${active}" data-color="${c.display}">${colorBlock}${c.display}</button>`
    })
    .join('')

  // Event delegation
  container.addEventListener('click', (e) => {
    const btn = e.target.closest('.variant-option')
    if (btn) selectVariantByColor(btn.dataset.color)
  })
}

/**
 * Render size options
 */
function renderSizes(sizes) {
  const container = document.getElementById('sizeQuickSelect')
  if (!container || sizes.length === 0) return

  container.innerHTML = sizes
    .map((s) => {
      const active = s.selected ? ' active' : ''
      return `<button class="variant-option${active}" data-size="${s.display}">${s.display}</button>`
    })
    .join('')

  container.addEventListener('click', (e) => {
    const btn = e.target.closest('.variant-option')
    if (btn) selectVariantBySize(btn.dataset.size)
  })
}

/**
 * Select variant based on color
 */
export function selectVariantByColor(colorName) {
  selectedColor = colorName
  const variant = findVariant({ color: colorName, size: selectedSize })
  if (variant) switchToVariant(variant, 'color', colorName)
}

/**
 * Select variant by size
 */
export function selectVariantBySize(sizeName) {
  selectedSize = sizeName
  const variant = findVariant({ color: selectedColor, size: sizeName })
  if (variant) switchToVariant(variant, 'size', sizeName)
}

/**
 * Find matching variant
 */
function findVariant({ color, size }) {
  return cachedVariants.find((v) => {
    const colorMatch = !color || matchColor(v.color, color)
    const sizeMatch = !size || matchSize(v.size, size)
    return colorMatch && sizeMatch
  })
}

/**
 * Switch to a variant
 */
function switchToVariant(variant, type, value) {
  const sdk = store.getState('design.sdkInstance')
  log.info(`Switching variant: ${type}=${value}`)

  // MMS selling price is product-level (FRONT 55% margin) — switching a variant
  // must NOT jump back to the SDS supplier reference price (variant.price). Use
  // the current product's MMS price, falling back to variant.price only when no
  // product-level price exists.
  const mmsPrice = store.getState('design.currentProduct')?.price ?? variant.price
  if (mmsPrice) updatePrice(mmsPrice)
  updateActiveState(type, value)

  store.setState('design.currentVariant', variant)

  // Switch the canvas to the chosen SDS variant whenever the SDK is present and
  // the variant carries an SDS identifier — regardless of whether the variant
  // list came from MMS catalog data (useMmsVariants). Previously the MMS path
  // suppressed switchVariant entirely, so the canvas never followed color/size
  // selection. Prefer the synced SDS variant product id (sds_product_id), fall
  // back to variant.id for legacy SDK-sourced variants.
  const sdsVariantId = variant.sds_product_id || variant.id
  if (sdk && sdsVariantId) {
    sdk.switchVariant(sdsVariantId, (result) => {
      if (result.status === 'success') {
        eventBus.emit(EVENTS.VARIANT_SELECT, variant)
      }
    })
  }
}

function matchColor(variantColor, targetColor) {
  if (typeof variantColor === 'object') {
    return variantColor.color_name === targetColor || variantColor.name === targetColor
  }
  return String(variantColor) === targetColor
}

function matchSize(variantSize, targetSize) {
  if (typeof variantSize === 'object') {
    return variantSize.name === targetSize || variantSize.title === targetSize
  }
  return fixSizeEncoding(String(variantSize)) === targetSize
}

function updatePrice(price) {
  const priceEl = document.getElementById('productPricePreview')
  if (priceEl && price) {
    priceEl.textContent = `$${parseFloat(price).toFixed(2)}`
  }
}

function updateActiveState(type, value) {
  const containerId = type === 'color' ? 'colorQuickSelect' : 'sizeQuickSelect'
  const container = document.getElementById(containerId)
  if (!container) return

  container.querySelectorAll('.variant-option').forEach((btn) => {
    const btnText = btn.textContent.trim()
    const isMatch = btnText === value || btnText.endsWith(value)
    btn.classList.toggle('active', isMatch)
  })
}

function getColorValue(colorName) {
  // Image-content colors, not themed UI chrome — this maps real-world product
  // color NAMES (fabric/material colors) to swatch hex values; they represent
  // the physical product and must not follow the app theme.
  const colorMap = {
    black: '#000000', white: '#FFFFFF', red: '#FF0000', blue: '#0000FF',
    green: '#008000', yellow: '#FFFF00', navy: '#000080', gray: '#808080',
    grey: '#808080', pink: '#FFC0CB', purple: '#800080', orange: '#FFA500',
    brown: '#8B4513', beige: '#F5F5DC', maroon: '#800000', olive: '#808000',
    teal: '#008080', cyan: '#00FFFF', magenta: '#FF00FF', coral: '#FF7F50',
    salmon: '#FA8072', khaki: '#C3B091', ivory: '#FFFFF0', cream: '#FFFDD0',
    tan: '#D2B48C', charcoal: '#36454F', burgundy: '#800020', lavender: '#E6E6FA',
    mint: '#98FF98', gold: '#FFD700', silver: '#C0C0C0',
    'light blue': '#ADD8E6', 'light gray': '#D3D3D3', 'light grey': '#D3D3D3',
    'dark blue': '#00008B', 'dark green': '#006400', 'dark red': '#8B0000',
    'dark gray': '#A9A9A9', 'dark grey': '#A9A9A9', 'royal blue': '#4169E1',
    'sky blue': '#87CEEB', 'forest green': '#228B22', 'hot pink': '#FF69B4',
    'light pink': '#FFB6C1', heather: '#B7C4CF', 'heather gray': '#9CA3AF',
    'heather grey': '#9CA3AF', indigo: '#4B0082', aqua: '#00FFFF',
    turquoise: '#40E0D0', sand: '#C2B280', stone: '#928E85', slate: '#708090',
    wine: '#722F37', rust: '#B7410E', mustard: '#FFDB58', plum: '#8E4585',
    lilac: '#C8A2C8', peach: '#FFCBA4', rose: '#FF007F', mauve: '#E0B0FF',
    taupe: '#483C32', ash: '#B2BEB5', natural: '#F5F0E1', oatmeal: '#D3C4A5',
    chocolate: '#7B3F00', espresso: '#3C1414', camel: '#C19A6B',
  }
  return colorMap[String(colorName).toLowerCase()] || null
}

function fixSizeEncoding(sizeStr) {
  if (!sizeStr || typeof sizeStr !== 'string') return sizeStr
  return sizeStr
    .replace(/(\d+)"?\s*\?\s*(\d+)/g, '$1"x$2')
    .replace(/(\d+)"?\s*[xX]\s*(\d+)/g, '$1"x$2')
    .replace(/\s+/g, ' ')
    .trim()
}

// Wire into Design mode lifecycle
eventBus.on(EVENTS.SDK_READY, () => {
  loadVariants()
})

eventBus.on(EVENTS.PRODUCT_CHANGE, () => {
  // Reset selections on product change
  selectedColor = null
  selectedSize = null
  cachedVariants = []
})

// Expose for backward compat (Design mode index.js exposes via window.SDSManager)
if (typeof window !== 'undefined') {
  window.SDSManager = window.SDSManager || {}
  window.SDSManager.selectVariantByColor = selectVariantByColor
  window.SDSManager.selectVariantBySize = selectVariantBySize
}

export default {
  loadVariants,
  selectVariantByColor,
  selectVariantBySize,
}
